import { ColorEnum } from '@/constants/color'
import { isAndroid, isIOS } from '@/constants/environment'
import CheckmarkIcon from '@/features/ui/icons/components/CheckmarkIcon'
import { knobVerticalOffset } from '@/features/ui/inputs/components/Switch/constants'
import { useAnimatedPosition } from '@/features/ui/inputs/components/Switch/parts/Knob/hooks/useAnimatedPosition'
import CircleIcon from '@/features/ui/inputs/components/Switch/parts/Knob/parts/CircleIcon'
import SimulatedShadow from '@/features/ui/inputs/components/Switch/parts/Knob/parts/SimulatedShadow'
import { FC } from 'react'
import { Platform, View } from 'react-native'
import Animated from 'react-native-reanimated'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface KnobProps {
    checked: boolean
    disabled: boolean
}

// {@link https://reactnative.dev/blog/2024/10/23/release-0.76-new-architecture#limitations-and-spec-deviations}
const Knob: FC<KnobProps> = ({ checked, disabled }) => {
    const { styles } = useStyles(stylesheet, { disabled })

    const animatedStyle = useAnimatedPosition(checked)

    return (
        <Animated.View style={[styles.knob, animatedStyle]}>
            {isAndroid && Number(Platform.Version) < 31 && <SimulatedShadow />}
            <CircleIcon
                style={styles.knobCircle}
                fill={styles.knobCircle.color}
                filter={
                    !isIOS
                        ? 'drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.12)) drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.14)) drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.20))'
                        : undefined
                }
            />
            {checked && (
                <View style={styles.checkmarkContainer}>
                    <CheckmarkIcon width="16" height="16" fill={styles.checkmark.color} />
                </View>
            )}
        </Animated.View>
    )
}

const stylesheet = createStyleSheet({
    knob: {
        flexShrink: 0,
        position: 'absolute',
        top: knobVerticalOffset,
    },
    checkmarkContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    knobCircle: {
        ...(isIOS && {
            shadowColor: ColorEnum.Black,
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.2,
            shadowRadius: 3,
        }),
        variants: {
            disabled: {
                true: {
                    color: ColorEnum.GRAY_200,
                },
                false: {
                    color: ColorEnum.WHITE,
                },
            },
        },
    },
    checkmark: {
        variants: {
            disabled: {
                true: {
                    color: ColorEnum.BRICK_200,
                },
                false: {
                    color: ColorEnum.BRICK_600,
                },
            },
        },
    },
})

export default Knob
